import { useState, useCallback, lazy, Suspense } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Spinner,
} from '@heroui/react';
import { Crop, Save } from 'lucide-react';
import { useCreateDrawingViewport } from '@/api/drawing-viewports';
import type { DrawingViewport } from '@estimator/shared';

const TileViewer = lazy(() => import('../../markup-viewer/TileViewer'));

interface ViewportDrawingModalProps {
  isOpen: boolean;
  onClose: () => void;
  pageId: string;
  projectId: string;
  knowledgeItemId: string;
  pageNumber: number;
  onViewportCreated: (viewport: DrawingViewport) => void;
}

export default function ViewportDrawingModal({
  isOpen,
  onClose,
  pageId,
  projectId,
  knowledgeItemId,
  pageNumber,
  onViewportCreated,
}: ViewportDrawingModalProps) {
  const [bounds, setBounds] = useState<[number, number, number, number] | null>(null);
  const [name, setName] = useState(`Section Cut - Page ${pageNumber}`);
  const createViewport = useCreateDrawingViewport();

  const handleCreateMarkup = useCallback((data: any) => {
    const geometry = data.geometry;
    if (!geometry || geometry.type !== 'Polygon') return;
    const coords: number[][] = geometry.coordinates[0];
    const xs = coords.map((c) => c[0]);
    const ys = coords.map((c) => c[1]);
    const extracted: [number, number, number, number] = [
      Math.min(...xs),
      Math.min(...ys),
      Math.max(...xs),
      Math.max(...ys),
    ];
    setBounds(extracted);
  }, []);

  const handleSave = async () => {
    if (!bounds) return;
    try {
      const vp = await createViewport.mutateAsync({
        projectId,
        page_id: pageId,
        name,
        viewport_type: 'section_cut',
        bounds,
      });
      onViewportCreated(vp);
      handleClose();
    } catch {
      // Error handled by mutation
    }
  };

  const handleClose = () => {
    setBounds(null);
    setName(`Section Cut - Page ${pageNumber}`);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="full"
      classNames={{
        base: 'bg-[var(--bg-surface)]',
        header: 'border-b border-[var(--border)]',
        footer: 'border-t border-[var(--border)]',
      }}
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-2" style={{ color: '#e67e22' }}>
          <Crop size={18} />
          Draw Viewport Region — Page {pageNumber}
        </ModalHeader>

        <ModalBody className="p-0 overflow-hidden" style={{ height: 'calc(100vh - 140px)' }}>
          <div className="relative w-full h-full">
            {/* Instruction banner */}
            {!bounds && (
              <div
                className="absolute top-3 left-1/2 -translate-x-1/2 z-10 px-4 py-2 rounded-lg text-xs font-medium"
                style={{
                  backgroundColor: 'rgba(230, 126, 34, 0.9)',
                  color: '#fff',
                }}
              >
                Click and drag to draw a rectangle around the section-cut detail
              </div>
            )}

            {bounds && (
              <div
                className="absolute top-3 left-1/2 -translate-x-1/2 z-10 px-4 py-2 rounded-lg text-xs font-medium flex items-center gap-3"
                style={{
                  backgroundColor: 'rgba(230, 126, 34, 0.9)',
                  color: '#fff',
                }}
              >
                <span>Region selected</span>
                <Button
                  size="sm"
                  variant="flat"
                  className="text-white min-w-0 h-6"
                  onPress={() => setBounds(null)}
                >
                  Redraw
                </Button>
              </div>
            )}

            <Suspense
              fallback={
                <div className="flex items-center justify-center h-full">
                  <Spinner size="lg" color="warning" />
                </div>
              }
            >
              <TileViewer
                pageId={pageId}
                markups={[]}
                projectId={projectId}
                knowledgeItemId={knowledgeItemId}
                onCreateMarkup={handleCreateMarkup}
                activeToolOverride="area-rect"
              />
            </Suspense>
          </div>
        </ModalBody>

        <ModalFooter className="flex items-center gap-3">
          <Input
            size="sm"
            label="Viewport Name"
            value={name}
            onValueChange={setName}
            className="max-w-xs"
            classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
          />
          <div className="flex-1" />
          <Button variant="light" onPress={handleClose}>
            Cancel
          </Button>
          <Button
            onPress={handleSave}
            isDisabled={!bounds || !name.trim()}
            isLoading={createViewport.isPending}
            startContent={<Save size={14} />}
            style={{ backgroundColor: '#e67e22', color: '#fff' }}
          >
            Save Viewport
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
